import React, { useState, useEffect } from 'react'
import {
  DollarSign,
  FileText,
  CreditCard,
  Clock,
  TrendingUp,
  TrendingDown,
  Activity,
  Shield,
  AlertCircle,
  CheckCircle,
  RefreshCw,
  Eye,
  ArrowUpRight,
  ArrowDownRight,
  Plus,
  BarChart3,
  Zap,
  Globe,
  Banknote
} from 'lucide-react'
import { mockApiService } from '../services/mockApiService'
import type { MockDashboardStats, MockPayment, MockProviderStatus } from '../services/mockApi'
import { InvoiceManagement } from './InvoiceManagement'
import '../styles/invoice-management.css'

interface ProfessionalDashboardProps {
  className?: string
}

type DashboardView = 'overview' | 'invoices'

export const ProfessionalDashboard: React.FC<ProfessionalDashboardProps> = ({ className = '' }) => {
  const [currentView, setCurrentView] = useState<DashboardView>('overview')
  const [dashboardStats, setDashboardStats] = useState<MockDashboardStats | null>(null)
  const [recentPayments, setRecentPayments] = useState<MockPayment[]>([])
  const [providerStatuses, setProviderStatuses] = useState<MockProviderStatus[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadDashboardData()
    
    // Refresh data every 30 seconds
    const interval = setInterval(loadDashboardData, 30000)
    return () => clearInterval(interval)
  }, [])

  const loadDashboardData = async () => {
    try {
      setError(null)
      
      const [statsResponse, paymentsResponse, providersResponse, settlementResponse] = await Promise.all([
        mockApiService.getDashboardStats(),
        mockApiService.getPayments({ limit: 5, page: 1 }),
        mockApiService.getProviderStatuses(),
        mockApiService.getSettlementAnalytics('30d')
      ])

      if (statsResponse.success) {
        setDashboardStats(statsResponse.data)
      }

      if (paymentsResponse.success) {
        setRecentPayments(paymentsResponse.data.payments)
      }

      if (providersResponse.success) {
        setProviderStatuses(providersResponse.data)
      }

      if (settlementResponse.success) {
        // Store settlement analytics for display
        console.log('Settlement analytics loaded:', settlementResponse.data)
      }
    } catch (err) {
      setError('Failed to load dashboard data')
      console.error('Dashboard data loading error:', err)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-HK', {
      style: 'currency',
      currency: 'HKD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }

  const formatPercentage = (value: number): string => {
    const sign = value >= 0 ? '+' : ''
    return `${sign}${value.toFixed(1)}%`
  }

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'healthy': return 'text-green-600'
      case 'degraded': return 'text-yellow-600'
      case 'down': return 'text-red-600'
      default: return 'text-gray-600'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy': return <CheckCircle className="w-4 h-4" />
      case 'degraded': return <AlertCircle className="w-4 h-4" />
      case 'down': return <AlertCircle className="w-4 h-4" />
      default: return <Activity className="w-4 h-4" />
    }
  }

  const getPaymentStatusColor = (status: string): string => {
    switch (status) {
      case 'confirmed': return 'professional-status--success'
      case 'pending': return 'professional-status--warning'
      case 'failed': return 'professional-status--error'
      case 'expired': return 'professional-status--error'
      default: return 'professional-status--info'
    }
  }

  if (currentView === 'invoices') {
    return (
      <div className={`professional-dashboard ${className}`}>
        {/* Navigation Header */}
        <div className="professional-dashboard__header">
          <div className="professional-dashboard__header-content">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setCurrentView('overview')}
                className="professional-btn professional-btn--secondary"
              >
                <BarChart3 className="w-4 h-4" />
                Back to Overview
              </button>
              <div>
                <h1 className="professional-heading-1">Business Dashboard</h1>
                <p className="professional-text">
                  Manage your invoices and payment operations.
                </p>
              </div>
            </div>
          </div>
        </div>
        
        <InvoiceManagement />
      </div>
    )
  }

  if (loading) {
    return (
      <div className={`professional-dashboard ${className}`}>
        <div className="professional-card">
          <div className="professional-card__content">
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 text-gray-400 animate-spin" />
              <span className="ml-3 text-gray-600">Loading dashboard...</span>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={`professional-dashboard ${className}`}>
        <div className="professional-card">
          <div className="professional-card__content">
            <div className="flex items-center justify-center py-12">
              <AlertCircle className="w-8 h-8 text-red-500" />
              <span className="ml-3 text-red-600">{error}</span>
              <button
                onClick={loadDashboardData}
                className="ml-4 professional-btn professional-btn--secondary"
              >
                <RefreshCw className="w-4 h-4" />
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={`professional-dashboard ${className}`}>
      {/* Dashboard Header */}
      <div className="professional-dashboard__header">
        <div className="professional-dashboard__header-content">
          <h1 className="professional-heading-1">Business Dashboard</h1>
          <p className="professional-text">
            Monitor your payment operations, settlement performance, and provider health in real-time.
          </p>
        </div>
        <div className="professional-dashboard__header-actions">
          <button
            onClick={() => setCurrentView('invoices')}
            className="professional-btn professional-btn--primary"
          >
            <Plus className="w-4 h-4" />
            Manage Invoices
          </button>
          <button
            onClick={loadDashboardData}
            className="professional-btn professional-btn--secondary"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <section className="professional-grid professional-grid--cols-4">
        <div className="professional-card professional-dashboard__stat-card">
          <div className="professional-dashboard__stat-content">
            <div className="professional-dashboard__stat-header">
              <span className="professional-dashboard__stat-title">Total Revenue</span>
              <div className="professional-dashboard__stat-icon">
                <DollarSign className="w-5 h-5" />
              </div>
            </div>
            <div className="professional-dashboard__stat-value">
              {formatCurrency(dashboardStats?.totalRevenue || 0)}
            </div>
            <div className="professional-dashboard__stat-change">
              <span className={`professional-dashboard__stat-change-value ${
                (dashboardStats?.revenueChange || 0) >= 0 
                  ? 'professional-dashboard__stat-change-value--positive' 
                  : 'professional-dashboard__stat-change-value--negative'
              }`}>
                {(dashboardStats?.revenueChange || 0) >= 0 ? (
                  <ArrowUpRight className="w-4 h-4" />
                ) : (
                  <ArrowDownRight className="w-4 h-4" />
                )}
                {formatPercentage(dashboardStats?.revenueChange || 0)}
              </span>
              <span className="professional-dashboard__stat-change-period">vs last month</span>
            </div>
          </div>
        </div>

        <div className="professional-card professional-dashboard__stat-card">
          <div className="professional-dashboard__stat-content">
            <div className="professional-dashboard__stat-header">
              <span className="professional-dashboard__stat-title">Total Payments</span>
              <div className="professional-dashboard__stat-icon">
                <CreditCard className="w-5 h-5" />
              </div>
            </div>
            <div className="professional-dashboard__stat-value">
              {dashboardStats?.totalPayments?.toLocaleString() || '0'}
            </div>
            <div className="professional-dashboard__stat-change">
              <span className={`professional-dashboard__stat-change-value ${
                (dashboardStats?.paymentsChange || 0) >= 0 
                  ? 'professional-dashboard__stat-change-value--positive' 
                  : 'professional-dashboard__stat-change-value--negative'
              }`}>
                {(dashboardStats?.paymentsChange || 0) >= 0 ? (
                  <ArrowUpRight className="w-4 h-4" />
                ) : (
                  <ArrowDownRight className="w-4 h-4" />
                )}
                {formatPercentage(dashboardStats?.paymentsChange || 0)}
              </span>
              <span className="professional-dashboard__stat-change-period">vs last month</span>
            </div>
          </div>
        </div>

        <div className="professional-card professional-dashboard__stat-card">
          <div className="professional-dashboard__stat-content">
            <div className="professional-dashboard__stat-header">
              <span className="professional-dashboard__stat-title">Avg Settlement Time</span>
              <div className="professional-dashboard__stat-icon">
                <Clock className="w-5 h-5" />
              </div>
            </div>
            <div className="professional-dashboard__stat-value">
              {dashboardStats?.averageSettlementTime || 0}m
            </div>
            <div className="professional-dashboard__stat-change">
              <span className="professional-dashboard__stat-change-value professional-dashboard__stat-change-value--positive">
                <ArrowDownRight className="w-4 h-4" />
                -15.2%
              </span>
              <span className="professional-dashboard__stat-change-period">faster than SWIFT</span>
            </div>
          </div>
        </div>

        <div className="professional-card professional-dashboard__stat-card">
          <div className="professional-dashboard__stat-content">
            <div className="professional-dashboard__stat-header">
              <span className="professional-dashboard__stat-title">Cost Savings</span>
              <div className="professional-dashboard__stat-icon">
                <TrendingUp className="w-5 h-5" />
              </div>
            </div>
            <div className="professional-dashboard__stat-value">
              {formatCurrency(dashboardStats?.savingsVsTraditional || 0)}
            </div>
            <div className="professional-dashboard__stat-change">
              <span className="professional-dashboard__stat-change-value professional-dashboard__stat-change-value--positive">
                <ArrowUpRight className="w-4 h-4" />
                +23.4%
              </span>
              <span className="professional-dashboard__stat-change-period">vs traditional</span>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Grid */}
      <div className="professional-grid professional-grid--cols-2">
        {/* Settlement Analytics */}
        <div className="professional-card">
          <div className="professional-card__header">
            <h3 className="professional-card__title">Settlement Performance</h3>
            <p className="professional-card__subtitle">Multi-rail settlement tracking and analytics</p>
          </div>
          <div className="professional-card__content">
            <div className="professional-dashboard__settlement-analytics">
              <div className="professional-dashboard__settlement-rails">
                <div className="professional-dashboard__settlement-rail">
                  <div className="professional-dashboard__settlement-rail-header">
                    <div className="professional-dashboard__settlement-rail-icon professional-dashboard__settlement-rail-icon--fps">
                      <Zap className="w-4 h-4" />
                    </div>
                    <div className="professional-dashboard__settlement-rail-info">
                      <span className="professional-dashboard__settlement-rail-name">FPS</span>
                      <span className="professional-dashboard__settlement-rail-desc">Faster Payment System</span>
                    </div>
                  </div>
                  <div className="professional-dashboard__settlement-rail-stats">
                    <div className="professional-dashboard__settlement-stat">
                      <span className="professional-dashboard__settlement-stat-value">12.5m</span>
                      <span className="professional-dashboard__settlement-stat-label">Avg Time</span>
                    </div>
                    <div className="professional-dashboard__settlement-stat">
                      <span className="professional-dashboard__settlement-stat-value">98.5%</span>
                      <span className="professional-dashboard__settlement-stat-label">Success Rate</span>
                    </div>
                    <div className="professional-dashboard__settlement-stat">
                      <span className="professional-dashboard__settlement-stat-value">{formatCurrency(125000)}</span>
                      <span className="professional-dashboard__settlement-stat-label">This Month</span>
                    </div>
                  </div>
                </div>
                
                <div className="professional-dashboard__settlement-rail">
                  <div className="professional-dashboard__settlement-rail-header">
                    <div className="professional-dashboard__settlement-rail-icon professional-dashboard__settlement-rail-icon--cips">
                      <Globe className="w-4 h-4" />
                    </div>
                    <div className="professional-dashboard__settlement-rail-info">
                      <span className="professional-dashboard__settlement-rail-name">CIPS</span>
                      <span className="professional-dashboard__settlement-rail-desc">Cross-border Interbank Payment</span>
                    </div>
                  </div>
                  <div className="professional-dashboard__settlement-rail-stats">
                    <div className="professional-dashboard__settlement-stat">
                      <span className="professional-dashboard__settlement-stat-value">4.2h</span>
                      <span className="professional-dashboard__settlement-stat-label">Avg Time</span>
                    </div>
                    <div className="professional-dashboard__settlement-stat">
                      <span className="professional-dashboard__settlement-stat-value">96.8%</span>
                      <span className="professional-dashboard__settlement-stat-label">Success Rate</span>
                    </div>
                    <div className="professional-dashboard__settlement-stat">
                      <span className="professional-dashboard__settlement-stat-value">{formatCurrency(89000)}</span>
                      <span className="professional-dashboard__settlement-stat-label">This Month</span>
                    </div>
                  </div>
                </div>
                
                <div className="professional-dashboard__settlement-rail">
                  <div className="professional-dashboard__settlement-rail-header">
                    <div className="professional-dashboard__settlement-rail-icon professional-dashboard__settlement-rail-icon--ecny">
                      <Banknote className="w-4 h-4" />
                    </div>
                    <div className="professional-dashboard__settlement-rail-info">
                      <span className="professional-dashboard__settlement-rail-name">e-CNY</span>
                      <span className="professional-dashboard__settlement-rail-desc">Digital Yuan</span>
                    </div>
                  </div>
                  <div className="professional-dashboard__settlement-rail-stats">
                    <div className="professional-dashboard__settlement-stat">
                      <span className="professional-dashboard__settlement-stat-value">&lt; 1m</span>
                      <span className="professional-dashboard__settlement-stat-label">Avg Time</span>
                    </div>
                    <div className="professional-dashboard__settlement-stat">
                      <span className="professional-dashboard__settlement-stat-value">99.2%</span>
                      <span className="professional-dashboard__settlement-stat-label">Success Rate</span>
                    </div>
                    <div className="professional-dashboard__settlement-stat">
                      <span className="professional-dashboard__settlement-stat-value">{formatCurrency(45000)}</span>
                      <span className="professional-dashboard__settlement-stat-label">This Month</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="professional-dashboard__settlement-summary">
                <div className="professional-dashboard__settlement-summary-item">
                  <Clock className="w-4 h-4 text-blue-500" />
                  <div>
                    <span className="professional-dashboard__settlement-summary-value">85% faster</span>
                    <span className="professional-dashboard__settlement-summary-label">than traditional banking</span>
                  </div>
                </div>
                <div className="professional-dashboard__settlement-summary-item">
                  <TrendingUp className="w-4 h-4 text-green-500" />
                  <div>
                    <span className="professional-dashboard__settlement-summary-value">{formatCurrency(8500)} saved</span>
                    <span className="professional-dashboard__settlement-summary-label">in fees this month</span>
                  </div>
                </div>
                <div className="professional-dashboard__settlement-summary-item">
                  <Shield className="w-4 h-4 text-purple-500" />
                  <div>
                    <span className="professional-dashboard__settlement-summary-value">3 rails active</span>
                    <span className="professional-dashboard__settlement-summary-label">maximum resilience</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Transactions */}
        <div className="professional-card">
          <div className="professional-card__header">
            <h3 className="professional-card__title">Recent Transactions</h3>
            <p className="professional-card__subtitle">Latest payment activities with settlement status</p>
          </div>
          <div className="professional-card__content">
            {recentPayments.length > 0 ? (
              <div className="space-y-4">
                {recentPayments.map((payment) => (
                  <div key={payment.id} className="professional-dashboard__transaction-item">
                    <div className="professional-dashboard__transaction-main">
                      <div className="professional-dashboard__transaction-icon">
                        <span className="professional-dashboard__transaction-stablecoin">
                          {payment.stablecoinType}
                        </span>
                      </div>
                      <div className="professional-dashboard__transaction-details">
                        <div className="professional-dashboard__transaction-invoice">
                          {payment.invoiceNumber}
                        </div>
                        <div className="professional-dashboard__transaction-meta">
                          {payment.provider} • {payment.settlementRail}
                        </div>
                      </div>
                    </div>
                    <div className="professional-dashboard__transaction-status">
                      <div className="professional-dashboard__transaction-amount">
                        {formatCurrency(payment.amount)}
                      </div>
                      <div className="professional-dashboard__transaction-statuses">
                        <div className={`professional-status ${getPaymentStatusColor(payment.status)}`}>
                          <span className="professional-status__indicator"></span>
                          {payment.status}
                        </div>
                        <div className="professional-dashboard__settlement-status">
                          {payment.status === 'confirmed' ? (
                            <div className="professional-dashboard__settlement-progress">
                              <div className="professional-dashboard__settlement-step professional-dashboard__settlement-step--active">
                                <RefreshCw className="w-3 h-3 animate-spin" />
                                <span>Settling to {payment.settlementRail}</span>
                              </div>
                            </div>
                          ) : payment.status === 'settled' ? (
                            <div className="professional-dashboard__settlement-complete">
                              <CheckCircle className="w-3 h-3 text-green-500" />
                              <span>Settled via {payment.settlementRail}</span>
                            </div>
                          ) : (
                            <div className="professional-dashboard__settlement-pending">
                              <Clock className="w-3 h-3 text-gray-400" />
                              <span>Awaiting payment</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                <div className="pt-4 border-t border-gray-200">
                  <button className="professional-btn professional-btn--secondary w-full">
                    <Eye className="w-4 h-4" />
                    View All Transactions
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No recent transactions</p>
              </div>
            )}
          </div>
        </div>

        {/* Provider Status Monitoring */}
        <div className="professional-card">
          <div className="professional-card__header">
            <h3 className="professional-card__title">Provider Health</h3>
            <p className="professional-card__subtitle">Real-time provider monitoring</p>
          </div>
          <div className="professional-card__content">
            <div className="space-y-4">
              {providerStatuses.map((provider) => (
                <div key={provider.provider} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getStatusColor(provider.status)}`}>
                      {getStatusIcon(provider.status)}
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">
                        {provider.provider}
                      </div>
                      <div className="text-sm text-gray-500">
                        {provider.supportedStablecoins.join(', ')}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`professional-status ${
                      provider.status === 'healthy' ? 'professional-status--success' :
                      provider.status === 'degraded' ? 'professional-status--warning' :
                      'professional-status--error'
                    }`}>
                      <span className="professional-status__indicator"></span>
                      {provider.status}
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      {provider.responseTime > 0 ? `${provider.responseTime}ms` : 'Offline'} • {provider.uptime.toFixed(1)}% uptime
                    </div>
                  </div>
                </div>
              ))}
              
              <div className="pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">System Health</span>
                  <div className="flex items-center gap-2">
                    <div className="professional-status professional-status--success">
                      <span className="professional-status__indicator"></span>
                      All Systems Operational
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <section className="professional-card">
        <div className="professional-card__header">
          <h3 className="professional-card__title">Quick Actions</h3>
          <p className="professional-card__subtitle">Common tasks and operations</p>
        </div>
        <div className="professional-card__content">
          <div className="professional-grid professional-grid--cols-3">
            <button 
              onClick={() => setCurrentView('invoices')}
              className="professional-dashboard__quick-action"
            >
              <div className="professional-dashboard__quick-action-icon professional-dashboard__quick-action-icon--green">
                <FileText className="w-6 h-6" />
              </div>
              <div className="professional-dashboard__quick-action-content">
                <h4 className="professional-dashboard__quick-action-title">Manage Invoices</h4>
                <p className="professional-dashboard__quick-action-description">
                  Create and manage invoices with payment links and QR codes
                </p>
              </div>
            </button>

            <a href="#" className="professional-dashboard__quick-action">
              <div className="professional-dashboard__quick-action-icon professional-dashboard__quick-action-icon--blue">
                <CreditCard className="w-6 h-6" />
              </div>
              <div className="professional-dashboard__quick-action-content">
                <h4 className="professional-dashboard__quick-action-title">Create Payment</h4>
                <p className="professional-dashboard__quick-action-description">
                  Start a new payment with multi-stablecoin support
                </p>
              </div>
            </a>

            <a href="#" className="professional-dashboard__quick-action">
              <div className="professional-dashboard__quick-action-icon professional-dashboard__quick-action-icon--purple">
                <TrendingUp className="w-6 h-6" />
              </div>
              <div className="professional-dashboard__quick-action-content">
                <h4 className="professional-dashboard__quick-action-title">View Analytics</h4>
                <p className="professional-dashboard__quick-action-description">
                  Analyze payment trends and performance metrics
                </p>
              </div>
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}